// src/devAuth.ts
//
// Local-development auth bypass. Lets a developer impersonate a seeded backend
// user without going through Firebase Identity Platform. The bypass is gated
// behind REACT_APP_DEV_AUTH=1, which is only set in personal .env.*.local files
// — never in committed env files — so production and dev-cloud builds compile
// every branch in this module as dead code.
//
// In prod (header path through API Gateway), the backend trusts the
// X-Endpoint-API-UserInfo header set by the gateway after verifying the Firebase
// JWT. Locally there is no gateway: hysteron_backend reads that header directly,
// so emitting it from the browser is sufficient to impersonate any user.

import type { User as FirebaseUser } from "firebase/auth";
import type { AppUser } from "features/auth/context/AuthContext";

export interface DevSeededUser {
  firebaseId: string;
  email: string;
  archetype: string;
  notes?: string;
}

// Wrapped in a NODE_ENV ternary so CRA/Terser strips the literal array (and all
// the seeded emails/ids/archetypes) out of production bundles entirely.
export const SEEDED_USERS: DevSeededUser[] =
  process.env.NODE_ENV !== "production"
    ? [
        { firebaseId: "local-user-seeded-00001", email: "user00001@hysteron.local", archetype: "healthy_follicular_30s" },
        { firebaseId: "local-user-seeded-00003", email: "user00003@hysteron.local", archetype: "postmenopausal_55" },
        { firebaseId: "local-user-seeded-00004", email: "user00004@hysteron.local", archetype: "on_hrt_48" },
        { firebaseId: "local-user-seeded-00006", email: "user00006@hysteron.local", archetype: "low_iron_28", notes: "flagged ferritin + transferrin sat" },
        { firebaseId: "local-user-seeded-00007", email: "user00007@hysteron.local", archetype: "healthy_follicular_30s" },
        { firebaseId: "local-user-seeded-00009", email: "user00009@hysteron.local", archetype: "postmenopausal_55" },
        { firebaseId: "local-user-seeded-00010", email: "user00010@hysteron.local", archetype: "on_hrt_48" },
      ]
    : [];

const LS_KEY = "hysteron.devAuth.firebaseId";
// Sentinel written on explicit logout so getDevAuthSession() does not silently
// re-impersonate from REACT_APP_DEV_AUTH_USER on the next read.
const LS_SIGNED_OUT = "__signed_out__";

// Hard guard: refuse to enable the bypass under a production build, even if
// someone ships `REACT_APP_DEV_AUTH=1` by accident (cmdline override, stray
// .env.production.local, etc). `npm run build` and `npm run build:dev` both
// set NODE_ENV=production via react-scripts, so the dev branch dies there
// regardless of the dev flag. Local `npm start` runs with NODE_ENV=development
// — that's the only context where the bypass can ever activate.
export function isDevAuthEnabled(): boolean {
  if (process.env.NODE_ENV === "production") return false;
  return process.env.REACT_APP_DEV_AUTH === "1";
}

export interface DevAuthSession {
  sub: string;
  email: string;
}

function findSeeded(firebaseId: string | null | undefined): DevSeededUser | null {
  if (!firebaseId) return null;
  return SEEDED_USERS.find((u) => u.firebaseId === firebaseId) ?? null;
}

export function getDevAuthSession(): DevAuthSession | null {
  if (!isDevAuthEnabled()) return null;

  let stored: string | null = null;
  try {
    stored = typeof window !== "undefined" ? window.localStorage.getItem(LS_KEY) : null;
  } catch {
    // localStorage can throw in sandboxed iframes; fall through
  }

  // Explicit logout — do not fall back to env default.
  if (stored === LS_SIGNED_OUT) return null;

  let selected: string | null = stored;

  // Fallback to env default so REACT_APP_DEV_AUTH_USER=<id> auto-impersonates
  // on first load (when nothing has ever been written to localStorage).
  if (!selected) {
    const envDefault = (process.env.REACT_APP_DEV_AUTH_USER || "").trim();
    if (envDefault) selected = envDefault;
  }

  const user = findSeeded(selected);
  if (!user) return null;
  return { sub: user.firebaseId, email: user.email };
}

export const DEV_AUTH_CHANGE_EVENT = "hysteron:devAuthChange";

export function setDevAuthSession(firebaseId: string | null): void {
  if (!isDevAuthEnabled() || typeof window === "undefined") return;
  try {
    if (firebaseId) {
      window.localStorage.setItem(LS_KEY, firebaseId);
    } else {
      // Write the sentinel rather than removing the key, so the env-var
      // fallback in getDevAuthSession() doesn't silently re-impersonate.
      window.localStorage.setItem(LS_KEY, LS_SIGNED_OUT);
    }
    window.dispatchEvent(new Event(DEV_AUTH_CHANGE_EVENT));
  } catch {
    // ignore
  }
}

// Mints the base64url-no-padding JSON blob the backend expects in
// X-Endpoint-API-UserInfo. The header value mirrors what API Gateway emits
// in prod after verifying the Firebase JWT.
export function buildUserInfoHeader(session: DevAuthSession): string {
  const json = JSON.stringify({ sub: session.sub, email: session.email });
  return btoa(json).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function attachDevAuthHeader(headers: Headers, session: DevAuthSession): void {
  headers.set("X-Endpoint-API-UserInfo", buildUserInfoHeader(session));
}

// Returns an object shaped enough like FirebaseUser to satisfy AuthContext
// consumers (route guards, profile pages). Anything that calls real Firebase
// SDK methods on this object will fail — but the only such call in the codebase
// is getIdToken, which is short-circuited by the dev branch in authFetch.
export function makeFakeFirebaseUser(session: DevAuthSession): AppUser {
  const stub = {
    uid: session.sub,
    email: session.email,
    emailVerified: true,
    displayName: null,
    isAnonymous: false,
    photoURL: null,
    providerData: [],
    providerId: "dev-auth",
    refreshToken: "",
    tenantId: null,
    metadata: {
      creationTime: undefined,
      lastSignInTime: undefined,
    },
    delete: async () => undefined,
    getIdToken: async () => `dev-impersonation:${session.sub}`,
    getIdTokenResult: async () => ({
      token: `dev-impersonation:${session.sub}`,
      claims: { sub: session.sub, email: session.email },
      authTime: "",
      issuedAtTime: "",
      expirationTime: "",
      signInProvider: "dev-auth",
      signInSecondFactor: null,
    }),
    reload: async () => undefined,
    toJSON: () => ({ uid: session.sub, email: session.email }),
  } as unknown as FirebaseUser;

  return stub as AppUser;
}
